
import React from "react";
import { Wallet, Video, UserPlus, Activity } from "lucide-react";
import { Card } from "@/components/ui/card";
import TimeRangeSelector, { TimeRange } from "@/components/TimeRangeSelector";
import StatusCard from "./StatusCard";
import SourceBreakdownChart from "./SourceBreakdownChart";
import VideoTrendChart from "./VideoTrendChart";
import SystemStatusCard from "./SystemStatusCard";
import { Skeleton } from "@/components/ui/skeleton";

interface DashboardData {
  totalLoggedTime: number;
  totalPaidUsers: number;
  totalVideos: number;
  newSignups: number;
  avgVideosPerUser: number;
  apiStatus: 'operational' | 'degraded' | 'down';
}

interface SourceData {
  name: string;
  value: number;
  color: string;
}

interface VideoTimelineData {
  name: string;
  videos: number;
}

interface OverviewTabProps {
  data: DashboardData;
  sourceBreakdownData: SourceData[];
  videoTimelineData: VideoTimelineData[];
  timeRange: TimeRange;
  onTimeRangeChange: (range: TimeRange) => void;
  isLoading?: boolean;
}

const OverviewTab = ({
  data,
  sourceBreakdownData,
  videoTimelineData,
  timeRange,
  onTimeRangeChange,
  isLoading = false
}: OverviewTabProps) => {
  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {Array.from({ length: 4 }).map((_, index) => (
            <Card key={index} className="p-4">
              <Skeleton className="h-4 w-24 mb-2" />
              <Skeleton className="h-8 w-16" />
            </Card>
          ))}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <Card className="p-6">
            <Skeleton className="h-4 w-full mb-4" />
            <Skeleton className="h-[250px] w-full rounded-md" />
          </Card>
          <Card className="md:col-span-2 p-6">
            <Skeleton className="h-4 w-full mb-4" />
            <Skeleton className="h-[250px] w-full rounded-md" />
          </Card>
        </div>

        <Card className="p-6">
          <Skeleton className="h-4 w-full mb-4" />
          <div className="flex gap-4">
            <Skeleton className="h-10 w-1/3" />
            <Skeleton className="h-10 w-1/3" />
            <Skeleton className="h-10 w-1/3" />
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatusCard
          title="Total Paid Users"
          value={data.totalPaidUsers}
          icon={Wallet}
        />
        <StatusCard
          title="Total Videos"
          value={data.totalVideos}
          icon={Video}
        />
        <StatusCard
          title={`Signups ${timeRange.label}`}
          value={data.newSignups}
          icon={UserPlus}
        />
        <StatusCard
          title="Avg. Videos/User"
          value={data.avgVideosPerUser}
          icon={Activity}
        />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-y-2 gap-x-6">
        <SourceBreakdownChart data={sourceBreakdownData} />
        <VideoTrendChart
          data={videoTimelineData}
          timeRangeLabel={timeRange.label}
        />
      </div>

      <SystemStatusCard
        apiStatus={data.apiStatus}
        lastChecked="5 minutes ago"
        uptime={99.8}
      />
    </div>
  );
};

export default OverviewTab;
